import { Button } from '@/components/ui-kit/button';
import { AnimatePresence, motion } from 'framer-motion';
import { LogIn, Menu, X } from 'lucide-react';
import React from 'react';


import { useActiveOrganization } from '@/graphql/queries/organizations';
import { PurchaseButton } from '@/modules/subscription/components/PurchaseButton';

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuthStore } from '@/state/store/auth';
import { useTranslation } from 'react-i18next';
import { LanguageSelector, ProfileMenu } from '../core';
import { OrgSwitcher } from './OrgSwitcher';
import { MobileAccountMenu } from './MobileAccountMenu';
import { useLanguageContext } from '@/i18n/language-context';
import { cn } from '@/lib/utils';

const SYSTEM_ADMIN_ORG_ID = import.meta.env.VITE_SYSTEM_ADMIN_ORG_ID ?? '';

const SignedIn = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <>{children}</>;
  }
  return null;
};
const SignedOut = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <>{children}</>;
  }
  return null;
};

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useTranslation();
  const { data: orgData } = useActiveOrganization();
  const { isAuthenticated, orgId } = useAuthStore();
  const { currentLanguage, setLanguage, availableLanguages } = useLanguageContext();

  // Two-language segmented toggle on mobile. If only one language is
  // configured we still render the chip so the row stays aligned.
  const langOptions = (availableLanguages ?? []).slice(0, 2);
  const closeMobileMenu = () => setMobileMenuOpen(false);
  const organization = orgData
    ? {
      name: orgData.Name,
      slug: orgData.Slug,
      logoUrl: orgData.LogoUrl,
    }
    : null;

  const isAdminOrg = !!orgId && orgId === SYSTEM_ADMIN_ORG_ID;
  const logoTo = isAdminOrg ? '/admin' : orgId ? `/org/${orgId}` : '/';

  return (
    <>
      <header className="sticky top-0 z-49 border-b border-border bg-[hsl(var(--bg))] backdrop-blur-sm">
        <nav
          className={` px-4 sm:px-6 lg:px-8 ${isAuthenticated ? 'w-full' : 'mx-auto max-w-7xl'}`}
        >
          <div className="flex h-16 items-center justify-between">
            {/* Logo - Show org logo if available */}
            <div className="flex items-center">
              <Link to={logoTo} className="flex items-center gap-2">
                {organization?.logoUrl ? (
                  <img
                    src={organization.logoUrl}
                    alt={organization.name}
                    width={48}
                    height={48}
                    className="h-10 w-10 md:h-12 md:w-12 rounded-lg object-contain"
                  />
                ) : (
                  <img
                    src="/Selise Legal Templates.svg"
                    alt="SELISE Legal Templates"
                    width={424}
                    height={241}
                    className="h-10 w-auto md:h-12"
                  />
                )}
              </Link>
            </div>

            {/* Desktop Navigation */}

            <div className="hidden md:flex md:items-center md:gap-8">
              <SignedIn>
                <PurchaseButton />
              </SignedIn>
              <LanguageSelector />

              {/* Auth - Sign In or User Button */}
              <SignedOut>
                <Button asChild variant="default" size="sm">
                  <Link to="/sign-in">{t('COMMON_SIGNIN')}</Link>
                </Button>
              </SignedOut>
              <SignedIn>
                <>
                  <OrgSwitcher />
                  <ProfileMenu />
                </>
              </SignedIn>
            </div>

            {/* Mobile menu button */}
            <div className="flex md:hidden">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md p-2 text-foreground hover:bg-card"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <span className="sr-only">{t('COMMON_OPENMAINMENU')}</span>
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile menu - Full screen overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-100 md:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            {/* Menu overlay */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="fixed inset-0 z-101 md:hidden bg-[hsl(var(--bg))] flex flex-col "
            >
              {/* Header with close button */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <div className="flex items-center gap-3">
                  {organization?.logoUrl ? (
                    <img
                      src={organization.logoUrl}
                      alt={organization.name}
                      width={40}
                      height={40}
                      className="h-10 w-10 rounded-lg object-contain"
                    />
                  ) : (
                    <img
                      src="/Selise Legal Templates.svg"
                      alt="SELISE Legal Templates"
                      width={424}
                      height={241}
                      className="h-8 w-auto"
                    />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  className="inline-flex items-center justify-center rounded-lg p-2 text-foreground hover:bg-muted transition-colors"
                  aria-label="Close menu"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Navigation items — mobile-native, no popovers/dropdowns */}
              <div className="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-6">
                {/* Language: segmented EN/DE toggle */}
                <section className="flex flex-col gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-low-emphasis">
                    {t('LANGUAGE', 'Language')}
                  </span>
                  <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
                    {langOptions.map((lang) => {
                      const active = lang.languageCode === currentLanguage;
                      return (
                        <button
                          key={lang.itemId ?? lang.languageCode}
                          type="button"
                          onClick={() => setLanguage(lang.languageCode)}
                          className={cn(
                            'rounded-md px-3 py-2 text-sm font-semibold transition-colors',
                            active
                              ? 'bg-background text-foreground shadow-sm'
                              : 'text-medium-emphasis hover:text-foreground'
                          )}
                        >
                          {lang.languageName ?? lang.languageCode}
                        </button>
                      );
                    })}
                  </div>
                </section>

                <SignedOut>
                  <Button
                    asChild
                    variant="default"
                    size="lg"
                    className="w-full justify-start gap-3"
                    onClick={closeMobileMenu}
                  >
                    <Link to="/sign-in">
                      <LogIn className="h-5 w-5" />
                      <span>{t('COMMON_SIGNIN')}</span>
                    </Link>
                  </Button>
                </SignedOut>

                <SignedIn>
                  {/* Organization: keep popover but close menu after selection */}
                  <section className="flex flex-col gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-low-emphasis">
                      {t('COMMON_ORGANIZATION', 'Organization')}
                    </span>
                    <div className="flex items-center justify-between rounded-xl px-4 py-3 bg-muted/50">
                      <span className="text-sm font-medium text-foreground truncate">
                        {organization?.name ?? t('SELECT_ORGANIZATION', 'Select Organization')}
                      </span>
                      <OrgSwitcher onSelect={closeMobileMenu} />
                    </div>
                  </section>

                  {/* Account: flat list of actions (no dropdown) */}
                  <section className="flex flex-col gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-low-emphasis">
                      {t('COMMON_ACCOUNT', 'Account')}
                    </span>
                    <MobileAccountMenu onNavigate={closeMobileMenu} />
                  </section>
                </SignedIn>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
